import { useMutation, useQuery } from "@tanstack/react-query";
import jwtDecode from "jwt-decode";
import { get, post, put } from "./request";
import { Storage } from "./storage";
import { API_DELIMITER } from "../utils/constants";
import { USER_PRIVILEGES } from "../components/pages/UserManagement/Roles/RoleConfiguration";

const AUTH_TOKEN = 'auth-token';
const USER_PRIVILEGES_KEY = 'user-privileges';
const USER_ROLE = 'user-role';
const IMPERSONATION_FLAG = 'impersonation-active';
const IMPERSONATED_BY = 'impersonated-by';
// Tab-local (sessionStorage) record of which token this tab's cached role/privileges actually
// belong to. See initializeTabSession() below — this is what lets a stale tab detect a token
// swapped out from under it even across a hard reload, which wipes isSessionInvalidated()'s
// in-memory flag but not sessionStorage.
const TAB_TOKEN_REF = 'tab-token-ref';
// Tab-local (sessionStorage) marker: "this tab's session was just cleared client-side"
// (cross-tab invalidation, a genuine 401, an explicit logout, or an ended impersonation —
// clearUserSession() below is the one choke point all of those go through). Its only job is
// to stop this tab from silently re-attaching itself to whatever token happens to be sitting
// in the shared AUTH_TOKEN slot the moment it lands on /login — see the "duplicate tab, log
// out, log in as someone else" hotfix-2.8.2 QA notes for why that matters: without this, a
// tab that was just told "you're logged out" would instead silently become a clone of
// whichever OTHER session is currently live (same TAB_TOKEN_REF as that other tab), so the
// next logout/idle-timeout from either tab would wipe the shared token for both. Cleared the
// moment this tab actually completes a fresh, explicit login (setUserSession()).
const FORCE_LOGOUT_FLAG = 'force-logout';

export function getAuthToken() {
    return Storage.getValue(AUTH_TOKEN);
}

export function getUserPrivileges() {
    const userPrivileges = Storage.getValue(USER_PRIVILEGES_KEY) || '';
    return userPrivileges.split(API_DELIMITER);
}

export function setUserRole(roles: any) {
  const role = roles[0];
  Storage.setValue(USER_ROLE, role.name);
  Storage.setValue(USER_PRIVILEGES_KEY, role.pages);
}

export function getUserRole() {
    return Storage.getValue(USER_ROLE) || '';
}

export function setAuthToken(token: string) {
    Storage.setValue(AUTH_TOKEN, token);
}

export function clearAuthToken() {
    Storage.removeValue([AUTH_TOKEN]);
}

export function setUserSession(token: string) {
  const parsedToken = parseToken(token);
  Storage.setValue(AUTH_TOKEN, token);
  if (parsedToken && parsedToken.roles) {
    setUserRole(parsedToken.roles);
  }
  Storage.setValue(TAB_TOKEN_REF, token);
  // A genuine, explicit login in THIS tab — whatever got this tab logged out no longer
  // applies to it.
  Storage.removeValue([FORCE_LOGOUT_FLAG]);
}

export function markForceLoggedOut() {
    Storage.setValue(FORCE_LOGOUT_FLAG, 'true');
}

export function wasForceLoggedOut() {
    return Storage.getValue(FORCE_LOGOUT_FLAG) === 'true';
}

// AUTH_TOKEN, IMPERSONATION_FLAG and IMPERSONATED_BY live in localStorage — the one physical
// slot shared by every tab of the origin — while the role/privileges/TAB_TOKEN_REF trio are
// per-tab sessionStorage (see storage.ts). That's fine as long as whichever tab clears the
// shared slot is actually the tab that owns what's in it right now. It stops being fine the
// moment a STALE tab clears its own (out-of-date) session: if a different tab has since logged
// in, switched user, or started/ended an impersonation session, the shared slot already holds
// THAT tab's live token — and blindly wiping it here would rip that still-active session out
// from under it, just because this tab happened to be the one that got here first (a stale
// tab's forced logout, or an idle-timeout firing in a tab that's already fallen behind — see
// the "duplicate impersonated tab" case in the hotfix-2.8.2 QA notes). So: only touch the
// shared keys if they still hold the very token THIS tab was using (TAB_TOKEN_REF); otherwise
// someone else has already claimed the slot and it's theirs to clear, not ours — just drop our
// own now-meaningless local view of it.
//
// That ownership check only works the FIRST time this runs for a given tab: this same
// function unconditionally deletes TAB_TOKEN_REF below, so a second call (very real in
// practice — request.ts's forceReLogin() calls this on every request the axios interceptor
// blocks, and a page mid-refetch on window focus fires several of those at once, no de-dup)
// would find ownToken already empty, take the "!ownToken" branch, and wipe the shared token
// unconditionally — even though by then it may belong to a completely different, still-live
// tab. FORCE_LOGOUT_FLAG (set at the end of this function) is what makes the wipe-or-not
// decision one-shot per tab: once this tab has already relinquished its session, every later
// call leaves the shared keys strictly alone, no matter what TAB_TOKEN_REF reads as by then.
export function clearUserSession() {
    const alreadyCleared = wasForceLoggedOut();
    const ownToken = Storage.getValue(TAB_TOKEN_REF);
    const currentSharedToken = getAuthToken();
    if (!alreadyCleared && (!ownToken || currentSharedToken === ownToken)) {
        Storage.removeValue([AUTH_TOKEN, IMPERSONATION_FLAG, IMPERSONATED_BY]);
    }
    Storage.removeValue([USER_PRIVILEGES_KEY, USER_ROLE, TAB_TOKEN_REF]);
    // This tab no longer has a session of its own. Mark it so (a) a subsequent visit to
    // /login doesn't treat a DIFFERENT, still-live tab's token — now sitting alone in the
    // shared slot — as this tab's own to silently pick back up, and (b) any further call to
    // this same function during this tab's lifetime (see above) never re-touches the shared
    // keys. See FORCE_LOGOUT_FLAG above.
    markForceLoggedOut();
}

// Master-password "Login as user" (impersonation) — set once the impersonation OTP is verified.
// There is no separate "admin session" to juggle: the impersonated user's token IS the session,
// this flag+impersonatedBy just drive the "Viewing as..." banner. Cleared automatically by clearUserSession().
export function setImpersonationSession(impersonatedBy: any) {
    Storage.setValue(IMPERSONATION_FLAG, 'true');
    Storage.setValue(IMPERSONATED_BY, JSON.stringify(impersonatedBy || {}));
}

export function isImpersonating() {
    return Storage.getValue(IMPERSONATION_FLAG) === 'true';
}

export function getImpersonatedBy(): any {
    try {
        return JSON.parse(Storage.getValue(IMPERSONATED_BY) || '{}');
    } catch (e) {
        return {};
    }
}

// Cross-tab session staleness. The auth token lives in localStorage (shared across every tab
// of the same origin); if it changes in ANOTHER tab (logout + re-login, or an ended
// impersonation session), this tab's token/role no longer match what's actually live for it
// server-side. Rather than silently reloading this tab onto whatever the new session happens
// to be — a surprise identity switch, and a good way to lose in-progress work — we leave its
// UI exactly as-is and just mark it stale. request.ts checks this flag before sending any
// authenticated request and, the moment this tab tries to do anything, short-circuits with a
// clean "you've been logged out" redirect instead of letting a mismatched request go out and
// come back as an unexplained 404.
let sessionInvalidatedInThisTab = false;

export function isSessionInvalidated() {
    return sessionInvalidatedInThisTab;
}

export function markSessionInvalidated() {
    sessionInvalidatedInThisTab = true;
}

// Once request.ts has actually acted on the staleness (cleared the session and routed to
// /login), the flag has done its job — clear it so a fresh login attempt from this same tab
// isn't itself blocked by the request gate above.
export function resetSessionInvalidated() {
    sessionInvalidatedInThisTab = false;
}

// Mount once at the app root.
export function watchCrossTabAuthChanges() {
    function handleStorageEvent(event: StorageEvent) {
        if (event.key !== AUTH_TOKEN || event.newValue === event.oldValue) {
            return;
        }
        markSessionInvalidated();
    }
    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
}

export function initializeTabSession() {
    const token = getAuthToken();
    if (!token) {
        return;
    }
    if (wasForceLoggedOut()) {
        // This tab was just told its session is over (cross-tab invalidation, a genuine 401,
        // or a manual/idle logout) and has no cached role of its own — do NOT treat that the
        // same as a brand-new tab and silently adopt whatever token now happens to occupy the
        // shared slot; it may well belong to a completely different, unrelated tab's session.
        // Wait for an actual, explicit login from this tab (setUserSession() clears this flag).
        return;
    }
    const currentRole = getUserRole();

    if (!currentRole) {
        // Brand-new tab (or one duplicated before ever picking up a role) — adopt this token.
        const parsedToken = parseToken(token);
        if (parsedToken && parsedToken.roles && parsedToken.roles.length > 0) {
            setUserRole(parsedToken.roles);
        }
        Storage.setValue(TAB_TOKEN_REF, token);
        return;
    }

    // This tab already has a cached role — but does it still belong to the CURRENT token?
    // watchCrossTabAuthChanges() catches a token swap while this tab is live, but that's only an
    // in-memory flag: a hard reload (or a freshly opened tab that inherited old sessionStorage —
    // e.g. via back/forward cache) loses it while leaving the cached role/privileges in place.
    // TAB_TOKEN_REF is the persisted fingerprint of which token this tab's role actually came
    // from, so this catches the same staleness even across a reload. Deliberately does NOT fire
    // for a same-tab "Login As" role switch (changeRole) — that doesn't touch the token, so
    // TAB_TOKEN_REF still matches.
    const lastKnownToken = Storage.getValue(TAB_TOKEN_REF);
    if (lastKnownToken && lastKnownToken !== token) {
        markSessionInvalidated();
    }
}

export function getUserDetails(_token = ''): any {
    const token = getAuthToken() || _token;
    const parsedToken = parseToken(token);
    return parsedToken;
}

export function parseToken(token: string): any {
    if (token) {
        try {
            return jwtDecode(token);
        } catch (e) {
            return null;
        }
    }
    return null;
}

export function useUserLogin(onSuccess?: any, onError?: any) {
    const { mutate: userLogin, error, isLoading } = useMutation(
        ['userLogin'],
        async ({ username, password, recaptchaToken }: any) => await post(`/users/sign_in`, {
          username: username,
          password: password,
          recaptcha_token: recaptchaToken
        }, null, false),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { userLogin, error, isLoading };
}

export function useValidateToken(token: string) {
    const { data, isFetching } = useQuery(
        ['validateToken', token],
        async () => await get(`/api/v1/auth/IsValidToken?token=${token}`),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!token
        }
    );
    return { status: (data || {}).data || {}, isFetching };
}

export function useValidateUrl(token: string) {
    const { data, isFetching } = useQuery(
        ['validateUrl', token],
        async () => await get(`/users/password/edit?reset_password_token=${token}`),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!token
        }
    );
    return { status: data, isFetching };
}

export function useChangePassword(onSuccess?: any, onError?: any) {
    const { mutate: changePassword, error } = useMutation(
        ['changePassword'],
        async ({ newPassword, token }: any) => {
            const url = `/users/password`;
            const _response = await put(url, { user: { password: newPassword, password_confirmation: newPassword, reset_password_token: token } });
            return _response
        },
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { changePassword, error };
}

export function useForgotPassword(onSuccess?: any, onError?: any) {
    const { mutate: forgotPassword, error } = useMutation(
        ['forgotPassword'],
        async ({ username }: any) => await post(`/users/password`, { user: { username: username } }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { forgotPassword, error };
}

export function useGenerateOTP(onSuccess?: any, onError?: any) {
    const { mutate: generateOTP, error, isLoading } = useMutation(
        ['generateOTP'],
        async ({ username }: any) => await get(`/api/v1/auth/GenerateOTP?username=${username}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { generateOTP, error, isLoading };
}

export function useLoginWithOtp(onSuccess?: any, onError?: any) {
    const { mutate: loginWithOtp, error, isLoading } = useMutation(
        ['updateLocation'],
        async ({ username, otp, token }: any) => await post(`/api/v1/auth/LoginWithOtp?username=${username}&loginOtp=${otp}`, {}, { Authorization: token }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { loginWithOtp, error, isLoading };
}

export function useVerifyOTP(onSuccess?: any, onError?: any) {
    const { mutate: verifyOTP, error, isLoading } = useMutation(
        ['verifyOTP'],
        async ({ username, otp }: any) => await post(`/users/verify_otp`, { username, otp: otp }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { verifyOTP, error, isLoading };
}

export function useVerifyImpersonationOTP(onSuccess?: any, onError?: any) {
    const { mutate: verifyImpersonationOTP, error, isLoading } = useMutation(
        ['verifyImpersonationOTP'],
        async ({ impersonation_session_id, otp }: any) => await post(`/users/verify_impersonation_otp`, {
            impersonation_session_id,
            otp
        }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { verifyImpersonationOTP, error, isLoading };
}

export function useResendOTP(onSuccess?: any, onError?: any) {
    const { mutate: resendOTP, error, isLoading } = useMutation(
        ['resendOTP'],
        async ({username}: any) => await post(`/users/resend_otp`, {username}),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { resendOTP, error, isLoading };
}

export function useUnlockAccount(onSuccess?: any, onError?: any) {
    const { mutate: unlockAccount, error, isLoading } = useMutation(
        ['unlockAccount'],
        async ({ unlock_token }: any) => await get(`/users/unlock?unlock_token=${unlock_token}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { unlockAccount, error, isLoading };
}

export function hasUserAccess(key: string) {
    const privileges = (Storage.getValue(USER_PRIVILEGES_KEY) || '').split(API_DELIMITER);
    return privileges.includes(key);
}

export function isAdmin() {
    return !hasUserAccess(USER_PRIVILEGES.SUBMITTER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.REVIEWER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)
}

export function isComplianceUser() {
    return hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)
        || hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);
}

export function useSSOCheck(onSuccess?: any, onError?: any) {
    const { mutate: checkSSO, error, isLoading } = useMutation(
        ['ssoCheck'],
        async ({ code }: any) => await get(`/api/v1/companies/sso_check?code=${code}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { checkSSO, error, isLoading };
}

export function useSSOInit(onSuccess?: any, onError?: any) {
    const { mutate: initSSO, error, isLoading } = useMutation(
        ['ssoInit'],
        async ({ code, redirectUrl }: any) => await post(`/api/v1/auth/saml/init`, {
            code: code,
            redirect_url: redirectUrl
        }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { initSSO, error, isLoading };
}

export function useCompanyLookup(onSuccess?: any, onError?: any) {
    const { mutate: lookupCompany, error, isLoading } = useMutation(
        ['companyLookup'],
        async ({ code }: any) => await get(`/api/v1/companies/sso_check?code=${code}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { lookupCompany, error, isLoading };
}

export function useLogout(onSuccess?: any, onError?: any) {
    const { mutate: logout, error, isLoading } = useMutation(
        ['logout'],
        async () => await post(`/api/v1/auth/logout`, {}),
        {
            onError,
            onSuccess: (response) => {
                clearUserSession();
                onSuccess && onSuccess(response);
            }
        }
    );
    return { logout, error, isLoading };
}

// Ends an impersonation session early. Unlike useLogout, this deliberately does NOT
// clear the session itself on success — the caller (ImpersonationBanner) clears client-side
// regardless of whether this call succeeds or fails, so the admin is never stuck mid-impersonation.
export function useEndImpersonation(onSuccess?: any, onError?: any) {
    const { mutate: endImpersonation, error, isLoading } = useMutation(
        ['endImpersonation'],
        async () => await post(`/users/end_impersonation`, {}),
        {
            onError,
            onSuccess: (response) => {
                onSuccess && onSuccess(response);
            }
        }
    );
    return { endImpersonation, error, isLoading };
}

export function useLogoutAll(onSuccess?: any, onError?: any) {
    const { mutate: logoutAll, error, isLoading } = useMutation(
        ['logoutAll'],
        async () => await post(`/api/v1/auth/logout_all`, {}),
        {
            onError,
            onSuccess: (response) => {
                clearUserSession();
                onSuccess && onSuccess(response);
            }
        }
    );
    return { logoutAll, error, isLoading };
}
